import { z } from "zod";

import { TRAFFIC_STATES, VEHICLE_PRESETS } from "../../types/traffic";
import type {
  CreatedShareReport,
  PublicShareReport,
  ShareReportApi,
} from "./ShareReportApi";

const publicShareReportSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{24}$/),
  profile: z.object({
    plate: z.string().min(1).max(8),
    vehiclePreset: z.enum(VEHICLE_PRESETS),
    colorId: z.string().min(3).max(24),
    badge: z.enum(["garage", "founding"]).nullable(),
  }),
  elapsedSeconds: z.number().int().min(0).max(86_400),
  distanceMiles: z.number().min(0).max(1_000),
  peakDriverCount: z.number().int().min(0).max(1_000_000),
  worstState: z.enum(TRAFFIC_STATES),
  enduredDelaySeconds: z.number().min(0).max(86_400),
  addedDelaySeconds: z.number().min(0).max(1_728),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
}) satisfies z.ZodType<PublicShareReport>;

const createdShareReportSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{24}$/),
  url: z.url(),
  imageUrls: z.object({
    og: z.url(),
    portrait: z.url(),
  }),
  report: publicShareReportSchema,
}) satisfies z.ZodType<CreatedShareReport>;

const fetchedShareReportSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{24}$/),
  report: publicShareReportSchema,
});

export class NetlifyShareReportApi implements ShareReportApi {
  public async create(report: Parameters<ShareReportApi["create"]>[0]) {
    return createdShareReportSchema.parse(
      await this.request("/api/share/create", {
        appearance: {
          vehiclePreset: report.profile.vehiclePreset,
          colorId: report.profile.colorId,
        },
        elapsedSeconds: report.elapsedSeconds,
        distanceMiles: report.distanceMiles,
        peakDriverCount: report.peakDriverCount,
        worstState: report.worstState,
        addedDelaySeconds: report.addedDelaySeconds,
      }),
    );
  }

  public async get(shareId: string): Promise<PublicShareReport> {
    const result = fetchedShareReportSchema.parse(
      await this.request(`/api/share/${encodeURIComponent(shareId)}`),
    );
    return result.report;
  }

  private async request(path: string, body?: unknown): Promise<unknown> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    try {
      const response = await fetch(path, {
        method: body === undefined ? "GET" : "POST",
        credentials: "same-origin",
        ...(body === undefined
          ? {}
          : {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }),
        signal: controller.signal,
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(`Share report request failed (${response.status}).`);
      }
      return value;
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
